import { UserProfileCompletionRing } from '@/components/account/UserProfile';
import { Button } from '@/components/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { showUserMessages } from '../../../app/redux/actions/userMatchActions';
import { setUserprofileCompletionState } from '../../../app/redux/actions/userProfileCompletionActions';
import { useAppDispatch } from '../../../app/redux/hooks';
import { UserMatch, UserMatchAction } from '../../../app/redux/reducers/userMatchesReducer';
import { User } from '../../../app/user/profile/profileUtils';
import { formatCountdown } from '../../../app/utils/date';
import { asyncTrackingEvent, asyncTrackingEventProps } from '../../../app/utils/tracking';
import CourseMessageCard from './CourseMessageCard';
import EducationProviderMessageCard from './EducationProviderMessageCard';
import EventMessageCard from './EventMessageCard';
import OpportunityMessageCard from './OpportunityMessageCard';
import OrganisationMessageCard from './OrganisationMessageCard';

interface findOutMoreProps extends React.HTMLAttributes<HTMLDivElement> {
  path: string;
  id: string;
  title: string;
}

export const findOutMore: any = ({ path, id, title }: findOutMoreProps) => {
  return (
    <Button
      icon
      variant="bare"
      size="xs"
      onClick={() => {
        const event: asyncTrackingEventProps = {
          track: 'audience_builder_find_out_more_click',
          trackAbacus: 'audience_builder_find_out_more_click',
          trackUrl: path,
          trackId: id,
          trackText: 'find out more'
        };
        asyncTrackingEvent(event);
      }}
    >
      {title && <>{title}</>}
      <FontAwesomeIcon icon={['far', 'external-link']} className={`w-3`} />
    </Button>
  );
};

interface messageCardUtilProps extends React.HTMLAttributes<HTMLDivElement> {
  content: any;
  options: any;
}
export const messageCardUtil: any = ({ content, options }: messageCardUtilProps) => {
  const title = content?.title ? <h1 className="font-semibold inline text-base">{content?.title}</h1> : null;
  const subHeader: JSX.Element = options?.subHeader;
  const pillContent = options?.pills
    ? options?.pills.map((pill: any, i: number) => {
        if (pill.text === null) {
          return null;
        }

        const pillClasses =
          'inline-flex px-3 py-1 bg-gray-200 rounded-lg space-x-1.5 items-center mt-1 mr-2 flex-shrink-0 max-w-2/3';
        const pillTextClasses =
          'text-xxs font-semibold tracking-wide uppercase overflow-hidden whitespace-nowrap max-w-xs';
        return (
          <li key={i} className={pillClasses} data-te-toggle="tooltip" title={pill.text} style={{ maxWidth: '80%' }}>
            {pill.icon && (
              <FontAwesomeIcon
                icon={[pill?.weight ? pill?.weight : 'fal', pill.icon]}
                className={`${pill?.width ? pill?.width : 'w-2'} center`}
              />
            )}
            <span className={pillTextClasses}>{pill.text}</span>
          </li>
        );
      })
    : null;

  return (
    <div className="max-w-2xl">
      <div className="rounded-2xl bg-gray-50 shadow p-4 space-y-3 overflow-hidden">
        {content?.type && (
          <div className="flex">
            <div className="flex items-center">
              <div className={`rounded-full h-3 w-3 ${options.entityColor ? 'bg-' + options.entityColor : ''}`} />
            </div>
            <div className="uppercase text-gray-700 text-xs font-semibold tracking-wide ml-1">
              {options.entityName.replaceAll('_', ' ')}
            </div>
          </div>
        )}
        <div className="flex mb-3">
          {content?.path ? (
            <a
              href={content?.path}
              target="_blank"
              className="flex justify-center"
              data-cy={`link:view-${options.entityName}`}
              rel="noopener noreferrer"
            >
              {title}
              {findOutMore(content?.path, content?.nid, content?.title)}
            </a>
          ) : (
            title
          )}
        </div>
        {subHeader}
        {pillContent && <div className="flex flex-wrap mt-0 break-words">{pillContent}</div>}
      </div>
    </div>
  );
};

interface messageOrgLogoProps extends React.HTMLAttributes<HTMLDivElement> {
  org: any;
}

export const MessageOrgLogo: any = ({ org }: messageOrgLogoProps) => {
  const src = org?.organisation?.logo?.path || org?.logo?.path;
  const alt = org?.orgainsation?.logo?.alt || org?.logo?.alt;

  return (
    src && (
      <img
        height={75}
        width={75}
        className="mx-auto bg-white"
        src={process.env.GATSBY_IMAGE_BASE_URL + src}
        alt={`Logo for ${alt ? alt : null}`}
      />
    )
  );
};

interface completeProfileProps extends React.HTMLAttributes<HTMLDivElement> {
  user: User;
}
export const CompleteProfile: any = ({ user }: completeProfileProps) => {
  const dispatch = useAppDispatch();

  const profileCompletePercent = user.profileCompletion;
  const isComplete = profileCompletePercent === 100;
  const message = isComplete
    ? 'You’ll receive messages based on your profile'
    : 'Completing your profile makes it easier for employers to message you';

  const onClickUserProfile = React.useCallback(() => {
    dispatch(showUserMessages(false));
    dispatch(setUserprofileCompletionState(true));
  }, []);

  return (
    <div className="flex flex-col place-items-center py-20">
      <div className="flex flex-col justify-center space-y-5">
        <Button variant="bare" onClick={onClickUserProfile}>
          <UserProfileCompletionRing user={user} compact={false} />
        </Button>
        <div className="flex flex-col place-items-center text-center">
          <div className="uppercase bg-gray-200 inline-flex mb-2 px-3 py-1 rounded-lg space-x-1.5 items-center flex-shrink-0 text-xxs font-semibold">
            Did you know?
          </div>
          <p className="text-center leading-7 text-sm max-w-[23rem]">{message}</p>
        </div>
        <div className="justify-center flex sm:justify-center cursor-pointer">
          <Button appearance="primary" variant="outline" size="sm" onClick={onClickUserProfile}>
            {user.profileCompletion === 100 ? `Review my profile` : `Complete my profile`}
          </Button>
        </div>
      </div>
    </div>
  );
};
interface MatchBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  match: UserMatch;
  handleTransition: (action: UserMatchAction) => void;
}

const MatchBody = ({ match, handleTransition }: MatchBodyProps) => {
  React.useEffect(() => {
    if (!match.seen) {
      handleTransition(UserMatchAction.Seen);
    }
  }, [match.seen]);

  let card = null;
  switch (match.contentType) {
    case 'opportunity':
      card = <OpportunityMessageCard content={match.content} />;
      break;
    case 'event':
      card = <EventMessageCard content={match.content} />;
      break;
    case 'course':
      card = <CourseMessageCard content={match.content} />;
      break;
    case 'organisation':
      card = <OrganisationMessageCard content={match.content} />;
      break;
    case 'education_provider':
      card = <EducationProviderMessageCard content={match.content} />;
      break;
  }
  return (
    <div className="flex flex-col px-10 py-5 space-y-5 h-[calc(100%_-_60px)] overflow-scroll pb-44">
      <div>
        {match?.createdAtDate && (
          <span className="font-semibold text-xxs tracking-wide uppercase text-gray-400">
            {formatCountdown(match?.createdAtDate, 'fulldate')}
          </span>
        )}
        <p>{match.message}</p>
      </div>
      {card}
    </div>
  );
};

export default MatchBody;
